import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Film, Headphones, Inbox, Loader2, Mic } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FilterPills } from '@/components/ui/filter-pills'
import { SearchInput } from '@/components/ui/search-input'
import { InfiniteSentinel } from '@/components/ui/infinite-sentinel'
import { SearchSelect, type SearchOption } from '@/components/ui/search-select'
import { ConfirmDialog } from '@/components/features/bookings/ConfirmDialog'
import { useMenu } from '@/lib/MenuContext'
import { sdk } from '@/lib/sdk'
import { formatClock } from '@/lib/player'
import type { LibraryTarget, MediaLibraryItem } from '@/lib/sdk-official/resources/media'
import type { MediaEpisode } from '@/lib/sdk-official/resources/media-shows'
import { MediaLibraryRow } from './MediaLibraryRow'
import { useMediaLibrary, type LibraryKind } from './useMediaLibrary'

/** A published episode picked to go in the Feed as its card. */
export interface PickedEpisode {
  id: string
  title: string
  showTitle: string
  mediaKind: 'audio' | 'video' | null
}

interface MediaLibraryPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** A fixed kind (an episode's audio slot); without it the person filters. */
  kind?: 'audio' | 'video'
  onPick: (item: MediaLibraryItem) => void
  /** The Feed: a published episode can be picked too (it goes as the episode's card). */
  onPickEpisode?: (episode: PickedEpisode) => void
  /** Where the file goes: the list keeps only what that place can take (the API refuses the rest). */
  target?: LibraryTarget
}

/**
 * What a place cannot take, by the API's `reusableIn` (`?for=` filters already; this guards an
 * older answer): an episode's file goes only to another episode.
 */
export const fitsTarget = (item: MediaLibraryItem, target: LibraryTarget | undefined): boolean => {
  if (!target) return true
  if (item.reusableIn) return item.reusableIn.includes(target)
  return item.source?.type !== 'mediaEpisode' || target === 'episode'
}

type Tab = 'files' | 'episodes'

/**
 * F-178 1b "Da biblioteca": the community's audios and videos (the team sees all of them, a member
 * only what they sent), with search, filter and "usado em N lugares". A file that sits in paid
 * content asks before it is published somewhere open.
 */
export const MediaLibraryPicker = ({ open, onOpenChange, kind, onPick, onPickEpisode, target }: MediaLibraryPickerProps) => {
  const { t } = useTranslation('media')
  const [tab, setTab] = React.useState<Tab>('files')
  const [filter, setFilter] = React.useState<LibraryKind>(kind ?? 'all')
  const [q, setQ] = React.useState('')
  const [pendingPaid, setPendingPaid] = React.useState<MediaLibraryItem | null>(null)
  const raw = useMediaLibrary({ kind: kind ?? filter, q, target, enabled: open && tab === 'files' })
  const library = { ...raw, items: raw.items.filter((item) => fitsTarget(item, target)) }

  React.useEffect(() => { if (!open) { setQ(''); setTab('files'); setPendingPaid(null) } }, [open])

  const choose = (item: MediaLibraryItem) => {
    /* An episode's file goes in the Feed as the episode's card, which keeps the episode's access: nothing opens. */
    const becomesCard = Boolean(onPickEpisode) && item.source?.type === 'mediaEpisode'
    if (item.paidOrigin && !becomesCard) { setPendingPaid(item); return }
    onPick(item)
    onOpenChange(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-4 overflow-hidden p-4 sm:p-6" data-testid="media-library-picker">
          <DialogHeader>
            <DialogTitle>{t('library.pickerTitle')}</DialogTitle>
            <DialogDescription>{t('library.pickerDescription')}</DialogDescription>
          </DialogHeader>

          {onPickEpisode && (
            <FilterPills
              aria-label={t('library.pickerTitle')}
              semantics="tabs"
              value={tab}
              onChange={setTab}
              options={[{ value: 'files', label: t('library.tabFiles') }, { value: 'episodes', label: t('library.tabEpisodes'), icon: Mic }]}
              data-testid="library-tabs"
            />
          )}

          {tab === 'files' ? (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <SearchInput value={q} onChange={setQ} placeholder={t('library.searchPlaceholder')} className="min-w-0 flex-1 basis-48" data-testid="library-search" />
                {!kind && (
                  <FilterPills
                    aria-label={t('library.kindFilter')}
                    value={filter}
                    onChange={setFilter}
                    options={[
                      { value: 'all', label: t('library.kind.all') },
                      { value: 'audio', label: t('library.kind.audio'), icon: Headphones },
                      { value: 'video', label: t('library.kind.video'), icon: Film },
                    ]}
                    data-testid="library-kind"
                  />
                )}
              </div>
              <div className="min-h-[12rem] min-w-0 flex-1 overflow-y-auto">
                <LibraryListBody library={library} filtered={Boolean(q) || (!kind && filter !== 'all')}
                  renderItem={(item) => <MediaLibraryRow key={item.id} item={item} as="button" onClick={() => choose(item)} />} />
              </div>
            </div>
          ) : (
            <EpisodeChooser onPick={(episode) => { onPickEpisode?.(episode); onOpenChange(false) }} />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingPaid !== null}
        onOpenChange={(next) => { if (!next) setPendingPaid(null) }}
        title={t('library.paidConfirmTitle')}
        body={t('library.paidConfirmBody')}
        confirmLabel={t('library.paidConfirm')}
        destructive={false}
        onConfirm={() => {
          const item = pendingPaid
          setPendingPaid(null)
          if (!item) return
          onPick(item)
          onOpenChange(false)
        }}
      />
    </>
  )
}

/** The four states of a library list (loading, error with retry, empty, list), shared with the team's screen. */
export const LibraryListBody = ({ library, filtered, renderItem }: {
  library: ReturnType<typeof useMediaLibrary>
  filtered: boolean
  renderItem: (item: MediaLibraryItem) => React.ReactNode
}) => {
  const { t } = useTranslation('media')
  if (library.state === 'loading') {
    return (
      <div className="space-y-2" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3 rounded-lg border border-border bg-surface p-3">
            <div className="h-11 w-11 animate-pulse rounded-lg bg-muted" />
            <div className="flex-1 space-y-2"><div className="h-4 w-1/2 animate-pulse rounded bg-muted" /><div className="h-3 w-2/3 animate-pulse rounded bg-muted" /></div>
          </div>
        ))}
      </div>
    )
  }
  if (library.state === 'error') {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface p-4">
        <p className="flex items-center gap-2 text-sm text-foreground-secondary"><AlertTriangle className="h-4 w-4 text-amber-500" />{t('library.failed')}</p>
        <Button variant="outline" size="sm" onClick={() => void library.reload()}>{t('common.retry')}</Button>
      </div>
    )
  }
  if (library.items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-surface px-4 py-10 text-center">
        <Inbox className="h-8 w-8 text-muted-foreground/60" aria-hidden />
        <p className="font-medium text-foreground">{filtered ? t('library.emptyFiltered') : t('library.empty')}</p>
        <p className="text-sm text-muted-foreground">{filtered ? t('library.emptyFilteredHint') : t('library.emptyHint')}</p>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {library.items.map(renderItem)}
      <InfiniteSentinel hasMore={library.hasMore} loading={library.loadingMore} onLoadMore={() => void library.loadMore()} count={library.items.length} />
    </div>
  )
}

/** The Feed's "Episódio": a show of a Podcast or Vídeos space this person sees, then one of its published episodes. */
const EpisodeChooser = ({ onPick }: { onPick: (episode: PickedEpisode) => void }) => {
  const { t } = useTranslation('media')
  const { openSections } = useMenu()
  const spaces = React.useMemo(
    () => openSections.flatMap((section) => (section.spaces ?? []).filter((space) => space.module === 'podcast' || space.module === 'videos')),
    [openSections]
  )
  const [shows, setShows] = React.useState<SearchOption[] | null>(null)
  const [showId, setShowId] = React.useState('')
  const [episodes, setEpisodes] = React.useState<MediaEpisode[] | null>(null)
  const [failed, setFailed] = React.useState(false)

  const loadShows = React.useCallback(async () => {
    setFailed(false)
    setShows(null)
    try {
      const lists = await Promise.all(spaces.map(async (space) => ({ space, result: await sdk.mediaShows.list(space.id) })))
      const options = lists.flatMap(({ space, result }) => result.items.map((show) => ({ value: show.id, label: show.title, description: space.title, group: space.title })))
      setShows(options)
      if (options.length === 1) setShowId(options[0].value)
    } catch {
      setFailed(true)
    }
  }, [spaces])

  React.useEffect(() => { void loadShows() }, [loadShows])

  React.useEffect(() => {
    if (!showId) return
    let alive = true
    setEpisodes(null)
    sdk.mediaShows.episodes(showId, { status: 'published', limit: 50 })
      .then((page) => { if (alive) setEpisodes(page.items) })
      .catch(() => { if (alive) setEpisodes([]) })
    return () => { alive = false }
  }, [showId])

  const showTitle = shows?.find((s) => s.value === showId)?.label ?? ''

  if (failed) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface p-4">
        <p className="flex items-center gap-2 text-sm text-foreground-secondary"><AlertTriangle className="h-4 w-4 text-amber-500" />{t('library.failed')}</p>
        <Button variant="outline" size="sm" onClick={() => void loadShows()}>{t('common.retry')}</Button>
      </div>
    )
  }
  if (shows === null) return <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" />{t('library.loadingShows')}</p>
  if (shows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-surface px-4 py-10 text-center">
        <Mic className="h-8 w-8 text-muted-foreground/60" aria-hidden />
        <p className="font-medium text-foreground">{t('library.noShows')}</p>
      </div>
    )
  }
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      <SearchSelect
        value={showId}
        onChange={setShowId}
        options={shows}
        placeholder={t('library.pickShow')}
        searchPlaceholder={t('library.searchShows')}
        emptyLabel={t('library.noShows')}
        testId="library-show-select"
      />
      <div className="min-h-[10rem] min-w-0 flex-1 overflow-y-auto">
        {!showId ? null : episodes === null ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" />{t('library.loadingEpisodes')}</p>
        ) : episodes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t('library.noEpisodes')}</p>
        ) : (
          <div className="space-y-2">
            {episodes.map((episode) => (
              <button key={episode.id} type="button" data-testid="library-episode"
                onClick={() => onPick({ id: episode.id, title: episode.title, showTitle, mediaKind: episode.mediaKind })}
                className="flex w-full min-w-0 items-center gap-3 rounded-lg border border-border bg-surface p-3 text-left transition-colors hover:border-primary hover:bg-primary-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary" aria-hidden>
                  {episode.mediaKind === 'video' ? <Film className="h-5 w-5" /> : <Headphones className="h-5 w-5" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-sm font-medium text-foreground">{episode.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {[episode.durationSeconds ? formatClock(episode.durationSeconds) : null, episode.isOpen ? t('library.episodeOpen') : null].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
